/**
 * Pure helpers for the Activity sidebar layout.
 *
 * Activity inverts the Projects layout: threads are the spine, and the project
 * picker is a filter over one flat list rather than a container for nested
 * ones. Three sections, in order:
 *
 *   1. Pinned  — threads the user pinned, held above everything else.
 *   2. Active  — everything still in flight, newest first.
 *   3. Settled — threads whose work is finished for now, parked in a collapsed
 *                shelf so the list stays short without hiding them outright.
 *                DERIVED, not stored: an explicit settle, a merged PR, or
 *                simple silence past the window all qualify, and any of them
 *                is undone the moment the thread has real activity again.
 *                See `threadSettled`.
 *
 * Kept side-effect free so the grouping and scope rules are unit-testable
 * without mounting the sidebar.
 */

import { effectiveSettled, type SettledOptions } from "./threadSettled";

/** Sentinel scope meaning "no project filter" — every thread is listed. */
const ACTIVITY_ALL_PROJECTS_SCOPE = null;

/**
 * The keys `ActivitySidebar` hands `renderThreadList` for each of its three
 * runs of rows.
 *
 * Exported because the archived run is the one that renders slim, and the
 * component choosing that variant is `ActiveThreadsSection` — a different file
 * from the one naming the section. Two string literals that must stay equal
 * across two files is exactly the pair that drifts silently: the shelf would
 * keep rendering, just as cards, with nothing to fail.
 */
export const ACTIVITY_SECTION_ID = {
  pinned: "__activity_pinned__",
  active: "__activity__",
  settled: "__activity_settled__",
} as const;

export interface ActivityScopeProject {
  readonly projectKey: string;
  readonly name: string;
  readonly environmentId: string;
  readonly id: string;
  /** Project root, used to resolve the project's favicon in the picker. */
  readonly cwd: string;
  /**
   * The CMMD-owned icon, when this project has one. Present exactly when the
   * Projects layout would draw a canonical icon for the same project, so the
   * picker and the project rows never disagree about what a project looks
   * like. Absent for plain local checkouts, which fall back to the favicon
   * discovered under `cwd`.
   */
  readonly canonicalIdentity?: {
    readonly iconUrl: string | null;
    readonly icon: string | null;
  };
  /**
   * The CMMD board behind this project, or `null` when the row cannot answer
   * for it. Drives the picker's settings gear, which opens the board rather
   * than the Forge workspace's thin checkout tab.
   */
  readonly cmmdProjectId?: number | null;
  readonly memberProjectRefs?: readonly {
    readonly environmentId: string;
    readonly projectId: string;
  }[];
}

/**
 * The projects the Activity scope picker offers, from the groups the Projects
 * layout renders as rows.
 *
 * Pure, and separate from the component, because this is the sidebar half of
 * the one question both project pickers answer: which projects may a user pick.
 * While it lived inline in `ActivitySidebarSection`'s memo no test could reach
 * it, and the `/new` picker grew a second answer that disagreed (FOR-36359).
 * Anything that decides pickability belongs here or in the shared predicate it
 * calls, never at a call site.
 *
 * A row is not always pickable. A desktop-local project that Forge can see but
 * cannot act on is a listing, not a target, and an archived board is not
 * somewhere work continues.
 */
export function buildActivityScopeProjects<
  TGroup extends {
    readonly groupKey: string;
    readonly displayName: string;
    readonly representative: {
      readonly environmentId: string;
      readonly id: string;
      readonly cwd: string;
      readonly source?: {
        readonly surface?: string;
        readonly actionAuthority?: string;
        readonly actionRouteAvailable?: boolean;
      } | null;
    };
    readonly memberProjectRefs: readonly {
      readonly environmentId: string;
      readonly projectId: string;
    }[];
  },
>(input: {
  readonly groups: readonly TGroup[];
  readonly archivedCmmdProjectIds: ReadonlySet<number>;
  readonly resolveCmmdProjectId: (group: TGroup) => number | null;
  readonly resolveCanonicalIdentity: (
    group: TGroup,
  ) => ActivityScopeProject["canonicalIdentity"] | null;
  readonly resolveDisplayName: (group: TGroup) => string | null | undefined;
}): ActivityScopeProject[] {
  return input.groups.flatMap((group) => {
    const source = group.representative.source;
    if (
      source?.surface === "desktop-local" &&
      source.actionAuthority === "inventory-only" &&
      !source.actionRouteAvailable
    ) {
      return [];
    }
    const cmmdProjectId = input.resolveCmmdProjectId(group);
    if (cmmdProjectId !== null && input.archivedCmmdProjectIds.has(cmmdProjectId)) return [];
    // Carry the group's canonical icon through. Dropping it here sent every
    // CMMD project down the favicon path, and a cloud project has no local cwd
    // to fetch one from, so the picker drew the same letter avatar for all of
    // them while the project rows below drew the real icons.
    const canonicalIdentity = input.resolveCanonicalIdentity(group);
    // Same story for the name: the group is named after its checkout, which for
    // anything imported from CMMD is a slug of the board title. Prefer the
    // chosen display name when the representative has one linked.
    const displayName = input.resolveDisplayName(group) ?? group.displayName;
    return [
      {
        projectKey: group.groupKey,
        name: displayName,
        environmentId: group.representative.environmentId,
        id: group.representative.id,
        cwd: group.representative.cwd,
        // The gear's destination. Null here is "ask the cwd link on click", not
        // "no board" — see `resolveProjectSettingsCmmdProjectId`.
        cmmdProjectId,
        ...(canonicalIdentity ? { canonicalIdentity } : {}),
        memberProjectRefs: group.memberProjectRefs.map((ref) => ({
          environmentId: ref.environmentId,
          projectId: ref.projectId,
        })),
      },
    ];
  });
}

export interface ActivityThreadInput {
  readonly id: string;
  readonly environmentId: string;
  readonly projectId: string;
  readonly kind?: string | null | undefined;
  readonly status?: string | null | undefined;
  readonly archivedAt?: string | null | undefined;
  readonly settledAt?: string | null | undefined;
  readonly settledOverride?: "settled" | "active" | null | undefined;
  /** Blocks settling: work has stopped and is waiting on a person. */
  readonly hasPendingApprovals?: boolean | undefined;
  readonly hasPendingUserInput?: boolean | undefined;
  readonly latestUserMessageAt?: string | null | undefined;
  readonly updatedAt?: string | null | undefined;
  readonly createdAt?: string | null | undefined;
}

export interface ActivityThreadSections<TThread> {
  readonly pinned: readonly TThread[];
  readonly active: readonly TThread[];
  readonly settled: readonly TThread[];
}

function physicalProjectKey(environmentId: string, projectId: string): string {
  return `${environmentId}:${projectId}`;
}

function scopedThreadIdentity(thread: ActivityThreadInput): string {
  return `${thread.environmentId}:${thread.id}`;
}

/**
 * Map every physical `(environmentId, projectId)` a project answers to onto
 * that project's logical key. A project grouped across environments owns
 * several physical keys, and a thread only ever carries one of them — without
 * this map, filtering to a grouped project would drop its remote threads.
 */
export function buildActivityProjectScopeIndex(
  projects: readonly ActivityScopeProject[],
): ReadonlyMap<string, string> {
  const index = new Map<string, string>();
  for (const project of projects) {
    index.set(physicalProjectKey(project.environmentId, project.id), project.projectKey);
    for (const member of project.memberProjectRefs ?? []) {
      index.set(physicalProjectKey(member.environmentId, member.projectId), project.projectKey);
    }
  }
  return index;
}

/**
 * Drop a scope key that no longer names a live project. The key is persisted
 * across sessions, so a project removed while it was the active filter would
 * otherwise leave the sidebar permanently empty with no visible cause.
 */
export function resolveActivityScopeKey(input: {
  readonly requestedScopeKey: string | null;
  readonly projects: readonly ActivityScopeProject[];
}): string | null {
  if (input.requestedScopeKey === null) return ACTIVITY_ALL_PROJECTS_SCOPE;
  return input.projects.some((project) => project.projectKey === input.requestedScopeKey)
    ? input.requestedScopeKey
    : ACTIVITY_ALL_PROJECTS_SCOPE;
}

/** Label for the scope trigger: the scoped project's name, or the all-projects text. */
export function resolveActivityScopeLabel(input: {
  readonly scopeKey: string | null;
  readonly projects: readonly ActivityScopeProject[];
  readonly allProjectsLabel: string;
}): string {
  if (input.scopeKey === null) return input.allProjectsLabel;
  return (
    input.projects.find((project) => project.projectKey === input.scopeKey)?.name ??
    input.allProjectsLabel
  );
}

/**
 * Archived is Forge's own lifecycle, not a derived one: the row's archive
 * action sets `archivedAt`, and this shelf is where those threads land. The
 * Projects layout drops archived threads entirely; Activity parks them instead
 * so the action has somewhere visible to go.
 */
export function isActivitySettledThread(
  thread: ActivityThreadInput,
  options: SettledOptions,
): boolean {
  // Activity treats Settle as the user's direct shelf toggle. This matches the
  // upstream sidebar and lets the row move without stopping work in progress.
  if (thread.settledOverride === "settled") return true;
  return effectiveSettled(thread, options);
}

/**
 * Split the visible threads into the three Activity sections, honouring the
 * project scope filter.
 *
 * Ordering in the live sections is delegated to `sortThreads` so Activity and
 * Projects stay on one sort rule (the user's `sidebarThreadSortOrder`). The
 * settled shelf is the exception. See `sortArchivedThreads`.
 * Workers are excluded: they render beneath their master row, exactly as they
 * do in the Projects layout.
 *
 * Settling outranks a pin: a settled thread leaves the pinned block, because
 * whatever settled it is the more recent and more specific instruction. A pin
 * is a standing preference; finishing the work is an event.
 */
export function buildActivityThreadSections<TThread extends ActivityThreadInput>(input: {
  readonly threads: readonly TThread[];
  readonly pinnedThreadById: Readonly<Record<string, boolean>>;
  readonly scopeKey: string | null;
  readonly projectKeyByPhysicalKey: ReadonlyMap<string, string>;
  readonly sortThreads: (threads: readonly TThread[]) => readonly TThread[];
  /** When a thread counts as settled. See `threadSettled`. */
  readonly settled: SettledOptions;
}): ActivityThreadSections<TThread> {
  const inScope = input.threads.filter((thread) => {
    if (thread.kind === "worker") return false;
    if (input.scopeKey === null) return true;
    const projectKey = input.projectKeyByPhysicalKey.get(
      physicalProjectKey(thread.environmentId, thread.projectId),
    );
    return projectKey === input.scopeKey;
  });

  const pinned: TThread[] = [];
  const active: TThread[] = [];
  const settled: TThread[] = [];
  for (const thread of inScope) {
    if (isActivitySettledThread(thread, input.settled)) {
      settled.push(thread);
      continue;
    }
    if (input.pinnedThreadById[scopedThreadIdentity(thread)] === true) {
      pinned.push(thread);
      continue;
    }
    active.push(thread);
  }

  return {
    pinned: input.sortThreads(pinned),
    active: input.sortThreads(active),
    settled: sortArchivedThreads(settled),
  };
}

/**
 * The one timestamp a settled row both sorts and labels by.
 *
 * The reference holds this invariant deliberately: a
 * history shelf where the order and the age labels are read from different
 * clocks looks like a sorting bug on every row where they disagree, and there
 * is no way to tell from the screen which of the two is lying.
 *
 * `archivedAt` first, because an explicit settle is when the work ended as far
 * as the user is concerned. It is now the *uncommon* case, though: most rows on
 * this shelf were never archived by hand — they merged, or simply went quiet —
 * and for those the fallbacks are not a degraded path but the normal one. They
 * are the same ladder the card ages from, so the order and the age labels stay
 * read from one clock.
 */
export function resolveArchivedTimestamp(thread: ActivityThreadInput): string | null {
  for (const candidate of [
    thread.archivedAt,
    thread.settledAt,
    thread.latestUserMessageAt,
    thread.updatedAt,
    thread.createdAt,
  ]) {
    if (candidate == null) continue;
    if (!Number.isNaN(Date.parse(candidate))) return candidate;
  }
  return null;
}

/**
 * Archived rows are history, so they order by when the work ended rather than
 * by the user's `sidebarThreadSortOrder` — that setting orders the live list,
 * where "most recently touched" and "newest" are both live questions. A shelf
 * of finished threads has only one useful order, and it is the one its own
 * labels report. Ties break on id so the order is total.
 */
export function sortArchivedThreads<TThread extends ActivityThreadInput>(
  threads: readonly TThread[],
): readonly TThread[] {
  const timestampMs = (thread: TThread) => {
    const timestamp = resolveArchivedTimestamp(thread);
    return timestamp === null ? 0 : Date.parse(timestamp);
  };
  return [...threads].toSorted(
    (left, right) => timestampMs(right) - timestampMs(left) || left.id.localeCompare(right.id),
  );
}

/**
 * Empty-state copy. The distinction matters: an empty list under a project
 * filter is a filter result, not an empty account, and telling the user which
 * one they are looking at is the difference between "add a project" and
 * "clear the filter".
 */
export function resolveActivityEmptyStateLabel(input: {
  readonly hasProjects: boolean;
  readonly scopedProjectName: string | null;
}): string {
  if (!input.hasProjects) return "No projects yet";
  if (input.scopedProjectName !== null) return `No threads in ${input.scopedProjectName} yet`;
  return "No threads yet";
}
